import axios from "@/utils/api";
import { clearAuthStorage, getStoredAuthToken } from "@/utils/authStorage";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref(null);
  const role = ref(null);
  const isAuthenticated = ref(false);
  const loading = ref(false);
  const error = ref(null);

  // Getters
  const userData = computed(() => user.value || null);
  const userRole = computed(() => role.value || null);
  const userName = computed(() => userData.value?.name || "Administrator");
  const userInstitutionId = computed(
    () =>
      userData.value?.institution_id ||
      userData.value?.userData?.institution_id ||
      null
  );
  const userFederalStateId = computed(
    () =>
      userData.value?.federal_state_id ||
      userData.value?.userData?.federal_state_id ||
      null
  );
  const userFederalStateName = computed(
    () => userData.value?.federal_state_name || null
  );
  const hasRole = computed(() => (role) => userRole.value === role);
  const hasAnyRole = computed(() => (roles) => roles.includes(userRole.value));

  // Actions
  const initializeAuth = () => {
    const storedAuth = localStorage.getItem("tvet_mis");
    if (storedAuth) {
      try {
        const authData = JSON.parse(storedAuth);
        user.value = authData;
        role.value = authData.role;
        isAuthenticated.value = true;
      } catch (err) {
        console.error("Error parsing stored auth data:", err);
        clearLocalSession();
      }
    }
  };

  /** Route name to open after successful auth, by API role name */
  const getDashboardRoute = (roleName) => {
    const routes = {
      "MoLSA Central Admin": { name: "molsa-admin-index" },
      Administrator: { name: "administrator-index" },
      "FMS TVET Officer": { name: "fms-admin-index" },
      "Institutional Admin": { name: "institution-dashboard" },
      Trainer: { name: "trainer-index" },
      Student: { name: "student-index" },
      Employer: { name: "employer-index" },
      Donor: { name: "donor-index" },
      Partner: { name: "admin-dashboard" },
    };
    return routes[roleName] || { name: "admin-dashboard" };
  };

  const persistAuth = (responseData) => {
    const authData = {
      name: responseData.name,
      token: responseData.token,
      role: responseData.role,
      institution_id: responseData.institution_id,
      federal_state_id: responseData.federal_state_id ?? null,
      federal_state_name: responseData.federal_state_name ?? null,
      userData: {
        id: responseData.id,
        email: responseData.email,
        institution_id: responseData.institution_id,
        federal_state_id: responseData.federal_state_id ?? null,
      },
    };

    user.value = authData;
    role.value = authData.role;
    isAuthenticated.value = true;

    localStorage.setItem("tvet_mis", JSON.stringify(authData));
    localStorage.setItem("name", authData.name);
    localStorage.setItem("role", authData.role);
    localStorage.setItem("auth_token", authData.token);

    return authData;
  };

  const login = async (credentials) => {
    loading.value = true;
    error.value = null;

    try {
      // Use the new v2 API endpoint
      const response = await axios.post("/api/v2/auth/login", {
        email: credentials.email,
        password: credentials.password,
        remember: credentials.remember || false,
      });

      const authData = persistAuth(response.data);

      return { success: true, data: authData };
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Login failed";
      const errorType = err.response?.data?.error || "general";

      error.value = errorMessage;

      // Create more specific error object for UI
      const loginError = new Error(errorMessage);
      loginError.type = errorType;
      loginError.response = err.response;

      throw loginError;
    } finally {
      loading.value = false;
    }
  };

  const register = async (payload) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await axios.post("/api/v2/auth/register", payload);
      const authData = persistAuth(response.data);

      return { success: true, data: authData, message: response.data.message };
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Registration failed";
      error.value = errorMessage;

      const registerError = new Error(errorMessage);
      registerError.response = err.response;
      throw registerError;
    } finally {
      loading.value = false;
    }
  };

  const clearLocalSession = () => {
    user.value = null;
    role.value = null;
    isAuthenticated.value = false;
    error.value = null;
    clearAuthStorage();
  };

  const logout = async () => {
    const token =
      user.value?.token || getStoredAuthToken();

    try {
      if (token) {
        await axios.post(
          "/api/v2/auth/logout",
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
    } catch (err) {
      console.error("Logout API error:", err);
      // Always clear the client session even when revoke fails (expired token, offline, etc.)
    } finally {
      clearLocalSession();
    }
  };

  /** Clear client session only (no API). */
  const clearAuth = () => {
    clearLocalSession();
  };

  const fetchProfile = async () => {
    loading.value = true;
    error.value = null;

    try {
      const { data: body } = await axios.get("/api/v2/auth/me");
      if (!body?.success) {
        throw new Error(body?.message || "Failed to load profile");
      }
      return body.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to load profile";
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const syncProfileToSession = (profile) => {
    if (!user.value || !profile) return;
    user.value.name = profile.full_name || profile.name;
    user.value.federal_state_id = profile.federal_state_id ?? user.value.federal_state_id;
    user.value.federal_state_name =
      profile.federal_state_name ?? user.value.federal_state_name;
    user.value.userData = {
      ...user.value.userData,
      id: profile.id,
      email: profile.email,
      institution_id: profile.institution_id,
      federal_state_id: profile.federal_state_id ?? user.value.userData?.federal_state_id,
      federal_state_name:
        profile.federal_state_name ?? user.value.userData?.federal_state_name,
    };
    if (profile.institution_id !== undefined) {
      user.value.institution_id = profile.institution_id;
    }
    localStorage.setItem("tvet_mis", JSON.stringify(user.value));
    localStorage.setItem("name", user.value.name);
  };

  const updateProfile = async (profileData) => {
    loading.value = true;
    error.value = null;

    try {
      const { data: body } = await axios.put(
        "/api/v2/auth/profile",
        profileData
      );
      if (!body?.success) {
        throw new Error(body?.message || "Profile update failed");
      }
      syncProfileToSession(body.data);
      return { success: true, data: body.data, message: body.message };
    } catch (err) {
      error.value = err.response?.data?.message || "Profile update failed";
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const changePassword = async (payload) => {
    loading.value = true;
    error.value = null;

    try {
      const { data: body } = await axios.post(
        "/api/v2/auth/change-password",
        payload
      );
      if (body?.success === false) {
        throw new Error(body?.message || "Password change failed");
      }
      return { success: true, message: body?.message || "Password changed" };
    } catch (err) {
      error.value = err.response?.data?.message || "Password change failed";
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const refreshToken = async () => {
    if (!isAuthenticated.value) return false;

    try {
      const response = await axios.post("/api/v2/auth/refresh");

      // Update stored token
      if (user.value && response.data.data.access_token) {
        user.value.token = response.data.data.access_token;
        user.value.expiresAt = response.data.data.expires_at;
        localStorage.setItem("tvet_mis", JSON.stringify(user.value));
      }

      return true;
    } catch (err) {
      console.error("Token refresh failed:", err);
      // Clear auth on refresh failure
      await logout();
      return false;
    }
  };

  // Check if user has permission for specific action
  const hasPermission = (permission) => {
    if (!userData.value) return false;
    return userData.value.permissions?.includes(permission) || false;
  };

  // Check if user can access route based on role
  const canAccessRoute = (route) => {
    if (!route.meta?.requiresAuth) return true;
    if (!isAuthenticated.value) return false;

    if (route.meta?.roles && !hasAnyRole.value(route.meta.roles)) {
      return false;
    }

    return true;
  };

  return {
    // State
    user,
    isAuthenticated,
    loading,
    error,

    // Getters
    userData,
    userRole,
    userName,
    userInstitutionId,
    userFederalStateId,
    userFederalStateName,
    hasRole,
    hasAnyRole,

    // Actions
    initializeAuth,
    login,
    register,
    getDashboardRoute,
    logout,
    clearAuth,
    fetchProfile,
    updateProfile,
    changePassword,
    syncProfileToSession,
    refreshToken,
    hasPermission,
    canAccessRoute,
  };
});
